import { FormEvent, useMemo, useState } from 'react';
import { LifeBuoy, Search, ShieldAlert } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import {
  SupportCandidate,
  useSupportImpersonationStore,
} from '@/features/support/stores/support-impersonation.store';

const buildCandidateLabel = (candidate: SupportCandidate) => {
  if (candidate.companyName && candidate.name) {
    return `${candidate.companyName} · ${candidate.name}`;
  }

  return candidate.companyName || candidate.name || 'Unnamed account';
};

export const SupportImpersonationSection = () => {
  const supportContext = useAuthStore((state) => state.supportContext);
  const {
    activeSession,
    searchResults,
    isSearching,
    isStarting,
    searchCandidates,
    startImpersonation,
    clearSearchResults,
  } = useSupportImpersonationStore();
  const [query, setQuery] = useState('');
  const [reason, setReason] = useState('');

  const canSearch = supportContext.allowlisted && supportContext.featureConfigured && !activeSession;

  const summaryText = useMemo(() => {
    if (!activeSession) {
      return null;
    }

    const pieces = [activeSession.targetEmail];
    if (activeSession.targetCompanyName) {
      pieces.push(activeSession.targetCompanyName);
    }
    return pieces.join(' · ');
  }, [activeSession]);

  if (!supportContext.allowlisted && !supportContext.isImpersonating) {
    return null;
  }

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    await searchCandidates(query);
  };

  const handleStart = async (candidate: SupportCandidate) => {
    const started = await startImpersonation(candidate, reason);
    if (started) {
      window.location.assign('/dashboard');
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
            <LifeBuoy className="h-5 w-5 text-blue-600" />
            Support Impersonation
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Internal-only tool. Search broker accounts, start a time-limited support session, and
            troubleshoot without replacing your Firebase login.
          </p>
        </div>
        <div className="rounded-full bg-blue-50 p-3">
          <ShieldAlert className="h-6 w-6 text-blue-600" />
        </div>
      </div>

      {!supportContext.featureConfigured ? (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Support impersonation is allowlisted for your account, but the backend is missing its
          secret or allowed-email configuration.
        </div>
      ) : null}

      {activeSession ? (
        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-4">
          <p className="text-sm font-medium text-blue-900">Active support session</p>
          <p className="mt-1 text-sm text-gray-700">{summaryText}</p>
          {activeSession.reason ? (
            <p className="mt-1 text-xs text-gray-600">Reason: {activeSession.reason}</p>
          ) : null}
          <p className="mt-2 text-xs text-gray-500">
            End the session from the yellow banner above the dashboard content to switch back to
            your own account.
          </p>
        </div>
      ) : null}

      <form className="mt-6 space-y-4" onSubmit={handleSearch}>
        <div className="grid gap-4 md:grid-cols-[1fr_220px]">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Search account</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Email, broker name, or company"
                className="w-full rounded-md border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                disabled={!canSearch}
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Reason</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="PDF QA for LendNav"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              disabled={!canSearch}
            />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSearch || isSearching}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSearching ? 'Searching...' : 'Search accounts'}
          </button>
          <button
            type="button"
            disabled={!query && searchResults.length === 0}
            onClick={() => {
              setQuery('');
              clearSearchResults();
            }}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </form>

      {searchResults.length > 0 ? (
        <div className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {searchResults.map((candidate) => (
            <div
              key={candidate.userId}
              className="flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-4 last:border-b-0 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <p className="font-medium text-gray-900">{candidate.email}</p>
                <p className="text-sm text-gray-600">{buildCandidateLabel(candidate)}</p>
              </div>
              <button
                type="button"
                disabled={isStarting || !!activeSession}
                onClick={() => handleStart(candidate)}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isStarting ? 'Starting...' : 'Impersonate'}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
